// Route chunks — and warming the next one before the player asks for it.
//
// Every page is `lazy()`-loaded (see lazyWithRetry), which keeps the first
// paint small but turns each navigation into a round trip: tap, wait for the
// chunk, THEN render. On venue wifi that is the spinner players see between
// screens, and it is worst on exactly the two steps that carry the most code:
//
//   - the lobby (WineSelect, and the picker's whole CTA/illustration set), and
//   - the reveal (WineReveal is the biggest chunk in the app by some distance —
//     GSAP, canvas-confetti, the bottle stage and the post-reveal composition
//     all hang off it).
//
// So the chunk for the next screen is fetched while the player is still on the
// current one, in the dead time they spend reading, typing a name, picking an
// avatar or dragging a rating slider. `import()` is memoised by the module
// loader, so when React.lazy later asks for the same module it resolves from
// memory — no network hop, and no Suspense fallback at all, because a lazy
// component whose promise has already settled renders in the same commit.
//
// The import factories live HERE rather than in App.jsx on purpose: a screen
// (or the router) can warm another screen's chunk without statically importing
// it, which is what would defeat the splitting in the first place. App.jsx
// wraps these same factories, so there is one set of chunks, not two.

import { onIdle } from './idle';

export const ROUTE_IMPORTS = {
  SessionEntry: () => import('../pages/SessionEntry'),
  Landing: () => import('../pages/Landing'),
  Join: () => import('../pages/Join'),
  AvatarSelect: () => import('../pages/AvatarSelect'),
  WineSelect: () => import('../pages/WineSelect'),
  Question: () => import('../pages/Question'),
  Rating: () => import('../pages/Rating'),
  AnswerComparison: () => import('../pages/AnswerComparison'),
  WineReveal: () => import('../pages/WineReveal'),
  ThankYou: () => import('../pages/ThankYou'),
  LeaderboardDisplay: () => import('../pages/LeaderboardDisplay'),
};

/** Warmed (or warming) chunk names, so nothing is fetched twice. */
const warmed = new Set();

// Warming is serialised. A handful of parallel chunk fetches on a congested
// venue connection competes with whatever the screen the player is ON still
// needs — the point is to use the spare capacity, not to claim it.
let queue = Promise.resolve();

/**
 * Fetch route chunks ahead of time, one at a time, each on an idle callback.
 *
 * Failures are swallowed and un-marked: a chunk that 404s because a deploy
 * landed mid-tasting must still go through lazyWithRetry's recovery (retry,
 * then a one-shot reload) when the player actually navigates there.
 *
 * @param {...keyof typeof ROUTE_IMPORTS} names
 */
export function warmRoutes(...names) {
  for (const name of names) {
    const load = ROUTE_IMPORTS[name];
    if (!load || warmed.has(name)) continue;
    warmed.add(name);

    queue = queue.then(
      () =>
        new Promise((resolve) => {
          onIdle(() => {
            Promise.resolve()
              .then(load)
              .catch(() => {
                warmed.delete(name);
              })
              .then(resolve);
          });
        })
    );
  }
}

/** Test seam — forget what has been warmed. */
export function resetWarmedRoutes() {
  warmed.clear();
  queue = Promise.resolve();
}
